import React, { useState, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import ContactModal from "../components/common/ContactModal";
import { navigationData } from "../data/navigationData";
import { assetPath } from "../utils/assetPath";

const Header = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const closeTimerRef = useRef(null);

  // Smooth dropdown open/close with debounce to prevent flicker
  const handleDropdownEnter = useCallback((label) => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setActiveDropdown(label);
  }, []);

  const handleDropdownLeave = useCallback(() => {
    closeTimerRef.current = setTimeout(() => {
      setActiveDropdown(null);
      closeTimerRef.current = null;
    }, 150);
  }, []);
  const [scrolled, setScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const location = useLocation();

  // Standardized theme for all sections
  const theme = {
    primary: "#134286",
    primaryBg: "bg-[#134286]/10",
    primaryText: "text-[#FFF]",
    accentGlow: "bg-[#134286]/10",
    dropdownBg: "bg-[#0a0f1c]/95",
    mobileBtn: "bg-[#134286]",
    mobileText: "text-[#134286]",
    mobileBg: "bg-white/5",
  };

  React.useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Scrolled state for background change
      setScrolled(currentScrollY > 20);

      // Visibility state for hiding on scroll down
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const { logo, navItems, cta } = navigationData;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-in-out ${
          isVisible ? "translate-y-0" : "-translate-y-full"
        } ${
          scrolled
            ? "bg-[#0a0f1c]/40  backdrop-blur-xl py-5"
            : "bg-linear-to-b from-black/90 to-transparent py-8"
        }`}
        role="banner"
      >
        <div className="max-w-[1400px] mx-auto px-6 flex items-center justify-between">
          {/* Logo (Left) */}
          <Link
            to="/"
            aria-label={`${logo.fullName} Logo`}
            className="flex items-center gap-3 relative z-60 shrink-0 group transition-all duration-500"
          >
            <div className="h-10 w-auto flex items-center justify-center transition-all duration-300">
              <img
                src={assetPath("/AEPL_lOGO.svg")}
                alt="AEPL Logo"
                className="h-18 w-auto group-hover:drop-shadow-[0_0_12px_rgba(0,163,255,0.6)] transition-all duration-300"
              />
            </div>
          </Link>

          {/* Navigation (Right) */}
          <div className="hidden lg:block">
            <nav
              aria-label="Main Navigation"
              className="flex items-center gap-2"
            >
              {navItems.map((item, index) => {
                let isActive = false;
                if (
                  item.label === "Industries" &&
                  location.pathname.startsWith("/industries")
                )
                  isActive = true;
                else if (
                  item.label === "Platform" &&
                  location.pathname.startsWith("/product")
                )
                  isActive = true;
                else if (
                  item.label === "About Us" &&
                  location.pathname.startsWith("/about")
                )
                  isActive = true;
                else if (location.pathname === item.path) isActive = true;

                return (
                  <div
                    key={index}
                    className="relative group h-full flex items-center"
                    onMouseEnter={() => handleDropdownEnter(item.label)}
                    onMouseLeave={handleDropdownLeave}
                  >
                    <div
                      className={`flex items-center gap-1.5 px-5 py-2 cursor-pointer text-[18px] font-medium tracking-wide transition-all duration-300 ${
                        activeDropdown === item.label || isActive
                          ? theme.primaryText
                          : "text-white/80 hover:text-white"
                      }`}
                    >
                      {item.label}
                      {item.children && (
                        <ChevronDown
                          className={`w-3.5 h-3.5 transition-transform duration-300 ${activeDropdown === item.label ? "rotate-180" : ""}`}
                        />
                      )}
                    </div>

                    {item.children && (
                      <div
                        className={`absolute top-full pt-5 z-100 transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                          item.label === "Industries"
                            ? "w-[720px] -right-[280px]"
                            : item.label === "Platform"
                              ? "w-[680px] -right-[220px]"
                              : "w-[520px] -right-[180px]"
                        } ${
                          activeDropdown === item.label
                            ? "opacity-100 translate-y-0 pointer-events-auto"
                            : "opacity-0 translate-y-2 pointer-events-none"
                        }`}
                      >
                        {item.label === "Industries" ? (
                          /* ── Industries Mega Menu ── */
                          <div className="bg-[#f0f2f5] rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.25)] p-6">
                            {/* Image Cards Row */}
                            <div className="grid grid-cols-3 gap-4 mb-5">
                              {item.children.map((group, gIdx) => {
                                const groupImages = {
                                  Aviation:
                                    assetPath("/IndustriesImages/Aviation/HeroAviation.webp"),
                                  Defence:
                                    assetPath("/IndustriesImages/Defence/DefenceHero.webp"),
                                  Energy:
                                    assetPath("/IndustriesImages/Energy/EnergyHero.webp"),
                                };
                                return (
                                  <Link
                                    key={gIdx}
                                    to={group.path || "#"}
                                    onClick={() => setActiveDropdown(null)}
                                    className="group relative block rounded-xl overflow-hidden aspect-[4/3] shadow-sm hover:shadow-lg transition-shadow duration-300"
                                  >
                                    <img
                                      src={groupImages[group.group]}
                                      alt={group.group}
                                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                    {/* Dark overlay at bottom for text */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                                    {/* Title */}
                                    <span className="absolute bottom-3 left-3 text-white font-bold text-[15px] tracking-wide drop-shadow-md">
                                      {group.group}
                                    </span>
                                    {/* Arrow icon */}
                                    <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/90 shadow-sm flex items-center justify-center group-hover:bg-white transition-colors duration-300">
                                      <ArrowRight className="w-3.5 h-3.5 text-slate-700 -rotate-45" />
                                    </div>
                                  </Link>
                                );
                              })}
                            </div>

                            {/* Sub-links Row */}
                            <div className="grid grid-cols-3 gap-0">
                              {item.children.map((group, gIdx) => (
                                <div
                                  key={gIdx}
                                  className={`flex flex-col gap-1 px-3 pt-2 ${
                                    gIdx < item.children.length - 1
                                      ? "border-r border-slate-300/60"
                                      : ""
                                  }`}
                                >
                                  {group.items.map((child, cIdx) => (
                                    <Link
                                      key={cIdx}
                                      to={child.path}
                                      onClick={(e) => {
                                        if (child.disabled) {
                                          e.preventDefault();
                                        } else {
                                          setActiveDropdown(null);
                                        }
                                      }}
                                      className={`px-3 py-2 text-[14px] font-medium rounded-lg transition-all duration-200 ${
                                        child.disabled
                                          ? "text-slate-400 cursor-not-allowed"
                                          : location.pathname === child.path
                                            ? "text-slate-900 bg-white font-semibold shadow-sm"
                                            : "text-slate-600 hover:text-slate-900 hover:bg-white/80"
                                      }`}
                                    >
                                      {child.label}
                                    </Link>
                                  ))}
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : item.label === "Platform" ? (
                          /* ── Platform Mega Menu ── */
                          <div className="bg-[#f0f2f5] rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.25)] p-6">
                            <div className="grid grid-cols-3 gap-4">
                              {item.children.map((child, cIdx) => {
                                const platformImages = {
                                  RAPTOR: assetPath("/PlatformImages/Raptor/RaptorBG.webp"),
                                  LearnOn:
                                    assetPath("/PlatformImages/LearnOn/LearnOnHero.webp"),
                                  Aurora:
                                    assetPath("/PlatformImages/Aurora/AuroraHero.webp"),
                                  "GRIFFIN XR":
                                    assetPath("/PlatformImages/GRIFFIN/GriffinHero.webp"),
                                  CourseAI:
                                    assetPath("/PlatformImages/CourseAI/CourseAIHero.webp"),
                                };
                                return (
                                  <Link
                                    key={cIdx}
                                    to={child.path}
                                    onClick={() => setActiveDropdown(null)}
                                    className={`group relative block rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300 ${
                                      cIdx >= 3 ? "col-span-1" : ""
                                    }`}
                                    style={{ aspectRatio: "4/3" }}
                                  >
                                    <img
                                      src={platformImages[child.label]}
                                      alt={child.label}
                                      loading="eager"
                                      fetchPriority="high"
                                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                                    <span className="absolute bottom-3 left-3 text-white font-bold text-[15px] tracking-wide drop-shadow-md">
                                      {child.label}
                                    </span>
                                    <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/90 shadow-sm flex items-center justify-center group-hover:bg-white transition-colors duration-300">
                                      <ArrowRight className="w-3.5 h-3.5 text-slate-700 -rotate-45" />
                                    </div>
                                  </Link>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          /* ── About Us Mega Menu ── */
                          <div className="bg-[#f0f2f5] rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.25)] p-6">
                            <div className="grid grid-cols-2 gap-4">
                              {item.children.map((child, cIdx) => {
                                const aboutImages = {
                                  "The Idea": assetPath("/About/Idea/IdeaHero.webp"),
                                  Quest: assetPath("/About/Quest/QuestHero.webp"),
                                  Leadership: assetPath("/About/Idea/Pic1.webp"),
                                  Crew: assetPath("/About/Quest/Pic1.webp"),
                                };
                                return (
                                  <Link
                                    key={cIdx}
                                    to={child.path}
                                    onClick={() => setActiveDropdown(null)}
                                    className="group relative block rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300"
                                    style={{ aspectRatio: "4/3" }}
                                  >
                                    <img
                                      src={aboutImages[child.label]}
                                      alt={child.label}
                                      loading="eager"
                                      fetchPriority="high"
                                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                                    <span className="absolute bottom-3 left-3 text-white font-bold text-[15px] tracking-wide drop-shadow-md">
                                      {child.label}
                                    </span>
                                    <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/90 shadow-sm flex items-center justify-center group-hover:bg-white transition-colors duration-300">
                                      <ArrowRight className="w-3.5 h-3.5 text-slate-700 -rotate-45" />
                                    </div>
                                  </Link>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Contact Us Link */}
              <div
                className="px-5 py-2 cursor-pointer text-[18px] font-medium tracking-wide text-white/80 hover:text-white transition-all duration-300"
                onClick={() => setIsContactModalOpen(true)}
              >
                Contact us
              </div>
            </nav>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden relative z-60 p-2 text-white hover:text-[#134286] transition-colors"
            aria-label="Toggle mobile menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, x: "100%" }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: "100%" }}
          className="fixed inset-0 z-40 bg-[#0a0f1c]/98 backdrop-blur-2xl lg:hidden flex flex-col pt-24 px-6 pb-6 overflow-y-auto"
        >
          <nav className="flex flex-col gap-2 w-full">
            {navItems.map((item, index) => (
              <div key={index} className="flex flex-col py-2">
                <div className="text-white/30 text-[10px] uppercase tracking-[0.2em] font-black mb-2 px-4">
                  {item.label}
                </div>
                {item.children ? (
                  <div className="flex flex-col gap-1">
                    {item.label === "Industries"
                      ? item.children.map((group, gIdx) => {
                          const groupPathPrefix = `/industries/${group.group.toLowerCase()}`;
                          const isGroupActive =
                            location.pathname.startsWith(groupPathPrefix);
                          return (
                            <div key={gIdx} className="mb-4">
                              <Link
                                to={group.path || "#"}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className={`flex items-center justify-between gap-2 text-[11px] font-black tracking-widest px-4 mb-2 transition-all duration-300 ${
                                  location.pathname.startsWith(
                                    `/industries/${group.group.toLowerCase()}`,
                                  )
                                    ? `${theme.primaryText} ${theme.mobileBg} rounded-lg py-1.5`
                                    : `${theme.primaryText}/80 hover:${theme.primaryText}`
                                }`}
                              >
                                <span>{group.group}</span>
                                <ArrowRight className="w-3.5 h-3.5 opacity-60" />
                              </Link>
                              <div className="flex flex-col gap-1">
                                {group.items.map((child, cIdx) => (
                                  <Link
                                    key={cIdx}
                                    to={child.path}
                                    onClick={() =>
                                      !child.disabled &&
                                      setIsMobileMenuOpen(false)
                                    }
                                    className={`px-4 py-2.5 text-base font-medium rounded-xl transition-all ${
                                      child.disabled
                                        ? "text-white/20"
                                        : location.pathname === child.path
                                          ? `${theme.primaryText} ${theme.mobileBg}`
                                          : "text-white/60 active:bg-white/5"
                                    }`}
                                  >
                                    {child.label}
                                  </Link>
                                ))}
                              </div>
                            </div>
                          );
                        })
                      : item.children.map((child, cIdx) => (
                          <Link
                            key={cIdx}
                            to={child.path}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={`px-4 py-3 text-base font-medium rounded-xl transition-all ${
                              location.pathname === child.path
                                ? `${theme.primaryText} ${theme.mobileBg}`
                                : "text-white/60 active:bg-white/5"
                            }`}
                          >
                            {child.label}
                          </Link>
                        ))}
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="px-4 py-2 text-xl font-bold text-slate-800 active:text-[#0084F9]"
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}

            <button
              onClick={() => {
                setIsMobileMenuOpen(false);
                setIsContactModalOpen(true);
              }}
              className={`mt-8 w-full border border-white/10 text-white font-bold text-base px-6 py-4 rounded-xl transition-all duration-300 flex justify-center items-center gap-2`}
              style={{ backgroundColor: theme.primary }}
            >
              {cta.label}
            </button>
          </nav>
        </motion.div>
      )}

      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
    </>
  );
};

export default Header;
